import type { WireEvent } from "@watchover/shared";
import type { WatchOverConfig } from "./config.js";
import { cssSelector, elementText, isMasked } from "./selector.js";
import { scrubUrl } from "./url.js";

/**
 * Zero-config interaction capture: clicks, committed input changes, scroll
 * depth, viewport size, and SPA navigations (history API + hash + popstate).
 * Everything is registered in the capture phase and throttled so host handlers
 * are never delayed and we never emit a flood.
 */
export class AutoCapture {
  private listeners: Array<() => void> = [];
  private lastScroll = 0;
  private lastViewport = 0;

  constructor(
    private cfg: WatchOverConfig,
    private emit: (event: WireEvent) => void,
    private onNavigate: () => void,
  ) {}

  start() {
    this.on(document, "click", this.handleClick, true);
    this.on(document, "change", this.handleChange, true);
    this.on(window, "scroll", this.handleScroll, { capture: true, passive: true });
    this.on(window, "resize", this.handleResize, { passive: true });
    this.patchHistory();
    this.on(window, "popstate", () => this.handleNavigation("popstate"));
    this.on(window, "hashchange", () => this.handleNavigation("hashchange"));

    this.emitViewport();
  }

  stop() {
    for (const off of this.listeners) off();
    this.listeners = [];
  }

  // ── handlers ────────────────────────────────────────────────────────────────

  private handleClick = (e: Event) => {
    const me = e as MouseEvent;
    const target = (me.target as Element) ?? null;
    if (!target || target.nodeType !== 1) return;
    const masked = isMasked(target, this.cfg.maskSelectors, this.cfg.unmaskSelectors);
    this.emit({
      t: "click",
      ts: Date.now(),
      selector: cssSelector(target),
      tag: target.tagName,
      text: elementText(target, masked),
      x: Math.round(me.clientX),
      y: Math.round(me.clientY),
    });
  };

  private handleChange = (e: Event) => {
    const el = e.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    if (!el || !("value" in el)) return;
    const masked = isMasked(el, this.cfg.maskSelectors, this.cfg.unmaskSelectors);
    const value = (el as HTMLInputElement).value ?? "";
    this.emit({
      t: "input",
      ts: Date.now(),
      selector: cssSelector(el),
      tag: el.tagName,
      inputType: (el as HTMLInputElement).type,
      masked,
      length: value.length,
      value: masked ? undefined : value.slice(0, 255),
    });
  };

  private handleScroll = () => {
    const now = Date.now();
    if (now - this.lastScroll < 250) return; // throttle to 4/s
    this.lastScroll = now;
    const doc = document.documentElement;
    const max = doc.scrollHeight - doc.clientHeight;
    const depth = max > 0 ? Math.min(1, doc.scrollTop / max) : 0;
    this.emit({
      t: "scroll",
      ts: now,
      x: window.scrollX,
      y: window.scrollY,
      depth: Math.round(depth * 1000) / 1000,
    });
  };

  private handleResize = () => {
    const now = Date.now();
    if (now - this.lastViewport < 500) return;
    this.lastViewport = now;
    this.emitViewport();
  };

  private emitViewport() {
    this.emit({
      t: "viewport",
      ts: Date.now(),
      w: window.innerWidth,
      h: window.innerHeight,
    });
  }

  private handleNavigation = (kind: string) => {
    const to = location.href;
    const fromClean = scrubUrl(this.lastUrl) ?? "";
    const toClean = scrubUrl(to) ?? location.pathname;
    this.emit({ t: "navigation", ts: Date.now(), from: fromClean, to: toClean, kind });
    this.lastUrl = to;
    // Emit a fresh pageview for the new virtual page.
    this.emit({
      t: "pageview",
      ts: Date.now(),
      url: toClean,
      path: location.pathname,
      title: document.title,
    });
    this.onNavigate();
  };

  private lastUrl = typeof location !== "undefined" ? location.href : "";

  /** Wrap history.pushState/replaceState to detect client-side route changes. */
  private patchHistory() {
    const fire = () => {
      // Defer so the framework finishes updating the URL/title first.
      setTimeout(() => this.handleNavigation("pushState"), 0);
    };
    const origPush = history.pushState;
    const origReplace = history.replaceState;
    history.pushState = function (this: History, ...args) {
      const r = origPush.apply(this, args as any);
      fire();
      return r;
    };
    history.replaceState = function (this: History, ...args) {
      const r = origReplace.apply(this, args as any);
      fire();
      return r;
    };
    this.listeners.push(() => {
      history.pushState = origPush;
      history.replaceState = origReplace;
    });
  }

  // ── util ──────────────────────────────────────────────────────────────────

  private on(
    target: EventTarget,
    type: string,
    handler: EventListenerOrEventListenerObject,
    opts?: boolean | AddEventListenerOptions,
  ) {
    target.addEventListener(type, handler, opts);
    this.listeners.push(() => target.removeEventListener(type, handler, opts));
  }
}
